/**
 * The form definition language.
 *
 * This is the schema of a schema: `formDefinitionSchema` validates what the organiser built, and
 * `buildSubmissionSchema` turns that definition into a Zod schema that validates what a
 * participant submitted. One definition, two uses — so the builder cannot produce a form the
 * server would reject, and the client and server validate submissions identically.
 */

import { z } from "zod";
import { instantSchema, shortTextSchema, ulidSchema } from "./primitives.js";

export const FIELD_TYPES = [
  "text",
  "long_text",
  "number",
  "date",
  "select",
  "multi_select",
  "checkbox",
  "phone",
  "email",
  "file",
  "consent",
  "heading",
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

export const fieldOptionSchema = z.object({
  value: z.string().min(1).max(200),
  label: shortTextSchema,
});

export const fieldValidationSchema = z.object({
  minLength: z.number().int().min(0).optional(),
  maxLength: z.number().int().min(1).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().max(200).optional(),
  patternMessage: shortTextSchema.optional(),
});

/**
 * Conditional visibility.
 *
 * A single condition against another field's value, not a general expression language. That is
 * enough for the case that actually recurs — a guardian block appearing for a youth age band —
 * and a general one would be a language an organiser has to learn.
 */
export const visibleWhenSchema = z.object({
  field: z.string().min(1),
  operator: z.enum(["equals", "not_equals", "includes", "is_empty", "is_not_empty", "lt", "gt"]),
  value: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

export const formFieldSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, "Use lowercase letters, numbers and underscores"),
  label: shortTextSchema,
  helpText: z.string().max(500).optional(),
  type: z.enum(FIELD_TYPES),
  required: z.boolean().default(false),
  options: z.array(fieldOptionSchema).optional(),
  validation: fieldValidationSchema.optional(),
  visibleWhen: visibleWhenSchema.optional(),
  sortOrder: z.number().int().min(0).default(0),
});

/**
 * Behaviour the organiser configures per form rather than per deploy (D3): what a registrant may
 * do after submitting, and the consent wording a submission is bound to. Stored inside the
 * versioned definition, so "edits were allowed when they registered" is answerable later.
 */
export const formSettingsSchema = z.object({
  confirmationMessage: z.string().trim().max(2000).optional(),
  consentText: z.string().trim().max(10_000).optional(),
  termsText: z.string().trim().max(10_000).optional(),
  allowEditsAfterSubmission: z.boolean().default(false),
  allowWithdrawal: z.boolean().default(false),
  allowMultiplePerPerson: z.boolean().default(false),
  opensAt: instantSchema.optional(),
  closesAt: instantSchema.optional(),
});

export const formDefinitionSchema = z
  .object({
    name: shortTextSchema,
    purpose: z.enum(["registration", "survey", "consent", "generic"]).default("registration"),
    fields: z.array(formFieldSchema).min(1),
    settings: formSettingsSchema.optional(),
  })
  .refine((form) => new Set(form.fields.map((f) => f.key)).size === form.fields.length, {
    message: "Field keys must be unique",
    path: ["fields"],
  })
  .refine(
    (form) =>
      form.fields.every(
        (field) =>
          !["select", "multi_select"].includes(field.type) || (field.options?.length ?? 0) > 0,
      ),
    { message: "A choice field needs at least one option", path: ["fields"] },
  )
  .refine(
    (form) => {
      const keys = new Set(form.fields.map((f) => f.key));
      return form.fields.every((f) => !f.visibleWhen || keys.has(f.visibleWhen.field));
    },
    { message: "A visibility condition points at a field that does not exist", path: ["fields"] },
  );

export type FormDefinition = z.infer<typeof formDefinitionSchema>;
export type FormFieldDefinition = z.infer<typeof formFieldSchema>;
export type VisibleWhen = z.infer<typeof visibleWhenSchema>;
export type FormSettings = z.infer<typeof formSettingsSchema>;

/** Metadata only — a form is created empty and its fields arrive as a draft version. */
export const createFormSchema = z.object({
  name: shortTextSchema,
  purpose: z.enum(["registration", "survey", "consent", "generic"]).default("registration"),
  eventId: ulidSchema.optional(),
  isTemplate: z.boolean().default(false),
});

export const updateFormSchema = z.object({
  name: shortTextSchema.optional(),
  purpose: z.enum(["registration", "survey", "consent", "generic"]).optional(),
  eventId: ulidSchema.nullable().optional(),
  isTemplate: z.boolean().optional(),
});

export const saveFormDraftSchema = z.object({
  definition: formDefinitionSchema,
});

export const publishFormVersionSchema = z.object({
  formVersionId: ulidSchema,
});
